import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { ArrowRightLeft, FileText, Users, Bot } from "lucide-react";

// ─── Event Categories ────────────────────────────────────────────────────────

export type ActivityEventCategory = "status" | "content" | "people" | "system";

// ─── Activity Event ──────────────────────────────────────────────────────────

export interface ActivityEvent {
  id: string;
  type: string;
  category: ActivityEventCategory;
  title: string;
  description?: string;
  actor: {
    name: string;
    avatarColor: string;
    initials: string;
    avatarUrl?: string;
  };
  timestamp: Date;
  relatedEntity?: {
    name: string;
    type: string;
  };
  icon?: LucideIcon;
  metadata?: Record<string, string>;
  /** Field-level diffs for update events — values are pre-formatted strings */
  changes?: Record<string, { from: string; to: string }>;
}

// ─── Per-event-type rendering overrides ──────────────────────────────────────

export interface EventTypeRenderer {
  /**
   * Replaces the entire body below the actor+title row.
   * Use this for fully custom event layouts.
   */
  renderContent?: (event: ActivityEvent) => ReactNode;

  /**
   * Replaces only the metadata pills row.
   * Use this when you want custom metadata display but keep the default layout.
   */
  renderMetadata?: (metadata: Record<string, string>) => ReactNode;
}

// ─── Filter Chip ─────────────────────────────────────────────────────────────

export interface ActivityFilterChip {
  id: string;
  label: string;
  category: ActivityEventCategory | "all";
  count: number;
}

// ─── Props ──────────────────────────────────────────────────────────────────

export interface ActivityFeedProps {
  items: ActivityEvent[];
  emptyTitle?: string;
  emptyDescription?: string;
  /** Enable URL persistence for filters */
  persistFiltersToUrl?: boolean;
  /** URL search params for filter persistence - pass the parsed search object from useSearch */
  urlSearchParams?: {
    categories?: string;
    search?: string;
  };
  /** Navigate function for URL updates */
  onUrlUpdate?: (params: { categories?: string; search?: string }) => void;
  /** Per-event-type custom renderers — keyed by activity type string */
  eventTypeRenderers?: Record<string, EventTypeRenderer>;
  /** Field label lookup for diff rows, e.g. { displayName: "Name" } */
  fieldLabels?: Record<string, string>;
}

// ─── Category Config ─────────────────────────────────────────────────────────

export interface ActivityCategoryConfig {
  color: string;
  bgColor: string;
  icon: LucideIcon;
  label: string;
}

export const ACTIVITY_CATEGORY_CONFIG: Record<
  ActivityEventCategory,
  ActivityCategoryConfig
> = {
  status: {
    color: "#E8734A",
    bgColor: "rgba(232, 115, 74, 0.15)",
    icon: ArrowRightLeft,
    label: "Status",
  },
  content: {
    color: "#3b82f6",
    bgColor: "rgba(59, 130, 246, 0.15)",
    icon: FileText,
    label: "Files",
  },
  people: {
    color: "#a855f7",
    bgColor: "rgba(168, 85, 247, 0.15)",
    icon: Users,
    label: "People",
  },
  system: {
    color: "#22c55e",
    bgColor: "rgba(34, 197, 94, 0.15)",
    icon: Bot,
    label: "AI",
  },
};
